import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";

// Serialize writes so an older response can never overwrite a newer snapshot.
export function useProjectAutosave(projectId, data, step, ready) {
  const [state, setState] = useState("loading");
  const latest = useRef(null);
  const acknowledged = useRef("");
  const queue = useRef(Promise.resolve());
  const timer = useRef(null);
  const mounted = useRef(true);
  const payload = JSON.stringify({ data, current_step: step });
  latest.current = ready && projectId ? { projectId, payload } : null;
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);

  const flush = useCallback(() => {
    clearTimeout(timer.current);
    const snapshot = latest.current;
    if (!snapshot) return Promise.resolve(false);
    const key = snapshot.projectId + snapshot.payload;
    if (acknowledged.current === key) return Promise.resolve(true);
    if (mounted.current) setState("saving");
    const operation = queue.current.catch(() => {}).then(async () => {
      if (acknowledged.current !== key) await api.put(`/projects/${snapshot.projectId}`, JSON.parse(snapshot.payload));
      acknowledged.current = key;
      const stillCurrent = latest.current?.projectId === snapshot.projectId && latest.current?.payload === snapshot.payload;
      if (stillCurrent) {
        try { sessionStorage.removeItem(`ks_recovery_${snapshot.projectId}`); } catch (_) { /* Browser storage may be unavailable. */ }
        if (mounted.current) setState("saved");
      }
      return true;
    }).catch(() => { if (mounted.current) setState("error"); return false; });
    queue.current = operation;
    return operation;
  }, []);

  useEffect(() => {
    if (!ready || !projectId) return;
    if (acknowledged.current === projectId + payload) return;
    setState("pending");
    try { sessionStorage.setItem(`ks_recovery_${projectId}`, JSON.stringify({ ...JSON.parse(payload), updatedAt: Date.now() })); } catch (_) { /* No false saved claim. */ }
    timer.current = setTimeout(flush, 800);
    return () => clearTimeout(timer.current);
  }, [projectId, payload, ready, flush]);

  useEffect(() => {
    const unload = (event) => {
      if (latest.current && acknowledged.current !== latest.current.projectId + latest.current.payload) {
        event.preventDefault(); event.returnValue = "";
      }
    };
    const leaving = () => { if (document.visibilityState === "hidden") flush(); };
    window.addEventListener("beforeunload", unload);
    document.addEventListener("visibilitychange", leaving);
    return () => { window.removeEventListener("beforeunload", unload); document.removeEventListener("visibilitychange", leaving); };
  }, [flush]);
  // Submit the last in-memory snapshot when navigation unmounts the form.
  useEffect(() => () => { flush(); }, [flush]);
  return { saveState: state, flush };
}